import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  ClipboardList,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useManagementReviews } from '@/hooks/use-management-reviews';
import { formatDateOnly } from '@/lib/date-utils';
import type { ManagementReviewListItem } from '@/lib/types';

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  scheduled: { className: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
  overdue: { className: 'bg-red-100 text-red-800', label: 'Overdue' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_BADGE[status] ?? { className: 'bg-gray-100 text-gray-800', label: status };
  return <Badge className={config.className}>{config.label}</Badge>;
}

function ActionItemsBadge({ review }: { review: ManagementReviewListItem }) {
  const { total, open, overdue } = review.action_items_summary;
  if (total === 0) return <span className="text-xs text-gray-400">None</span>;
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-600">{total} total</span>
      {open > 0 && <Badge variant="outline" className="text-xs">{open} open</Badge>}
      {overdue > 0 && <Badge className="bg-red-100 text-red-800 text-xs">{overdue} overdue</Badge>}
    </div>
  );
}

export function ReviewsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const { data: reviews, isLoading } = useManagementReviews({
    status: statusFilter || undefined,
  });

  const scheduledCount = reviews?.filter((r) => r.status === 'scheduled').length ?? 0;
  const completedCount = reviews?.filter((r) => r.status === 'completed').length ?? 0;
  const overdueCount = reviews?.filter((r) => r.status === 'overdue').length ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Management Reviews
          </h1>
          <p className="text-gray-600 mt-1">
            QP-300 Clause 8.2 — Leadership engagement and management review tracking
          </p>
        </div>
        <Button asChild>
          <Link to="/compliance/reviews/new">
            <Plus className="h-4 w-4 mr-2" />
            Schedule Review
          </Link>
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ClipboardList className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{reviews?.length ?? '-'}</p>
                <p className="text-sm text-gray-600">Total Reviews</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{scheduledCount}</p>
                <p className="text-sm text-gray-600">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={overdueCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{overdueCount}</p>
                <p className="text-sm text-gray-600">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Clock className="h-4 w-4 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : !reviews || reviews.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No management reviews yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Schedule your first management review to demonstrate leadership engagement
            </p>
            <Button asChild className="mt-4">
              <Link to="/compliance/reviews/new">
                <Plus className="h-4 w-4 mr-2" />
                Schedule Review
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Chairperson</TableHead>
                <TableHead>Action Items</TableHead>
                <TableHead className="hidden md:table-cell">Next Review</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.map((review) => (
                <TableRow key={review.id} className="hover:bg-gray-50">
                  <TableCell>
                    <Link
                      to={`/compliance/reviews/${review.id}`}
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {review.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={review.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDateOnly(review.date)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {review.chairperson_username || '-'}
                  </TableCell>
                  <TableCell>
                    <ActionItemsBadge review={review} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-gray-600">
                    {formatDateOnly(review.next_review_date, '-')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
